// @flow
import React, { useRef } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import {
  getConfiguredMetricRowsFromFormikValuesForEstimateCC,
  isGivenMetricApplicableForEstimateCC,
} from '@components/committed-capacity/utils';
import { useEstimateRateCardVersion } from '@src/custom-hooks/useEstimateRateCardVersion';
import { useIsEstimateRenewal } from '@src/custom-hooks/useIsEstimateRenewal';
import { EstimateCommittedCapacityDescriberContainer } from '@components/committed-capacity/EstimateCommittedCapacityDescriberContainer';
import { EstimateCommittedCapacityAddNewMetricButton } from '@components/committed-capacity/EstimateCommittedCapacityAddNewMetricButton';
import { Spacer } from '@presentational/spacing/Spacer';
import { Divider, Grid } from 'semantic-ui-react';
import { ESTIMATE_COMMITTED_CAPACITY } from '@components/committed-capacity/enums';
import { AddMetricNameDropdownRowForEstimateCC } from '@components/committed-capacity/AddMetricNameDropdownRowForEstimateCC';
import { AddNewMetricRowRelatedRowForEstimateCC } from '@components/committed-capacity/AddNewMetricRowRelatedRowForEstimateCC';
import { CommittedCapacityDimensionsGridForEstimateCC } from '@components/committed-capacity/CommittedCapacityDimensionsGridForEstimateCC';
import { FORM_NAMES } from '@streaming-projects/enums';
import { isAnythingEditedOnEstimatePageBesidesTheGivenForm } from '@components/views/utils';

import { If } from '../presentational/If';
import { PaddedAndRaisedSegment } from '../presentational/PaddedAndRaisedSegment';

const MetricRows = ({ arrayHelpers, configuredMetricRows, metricDimensionRefs }) => {
  const rateCardVersion = useEstimateRateCardVersion();
  const isRenewal = useIsEstimateRenewal();
  metricDimensionRefs.current.metricLevelArrayHelpers = arrayHelpers;

  return configuredMetricRows.map((row, index) => (
    <If
      key={`${row.id}-${index}`}
      test={
        row.metricName == null ||
        isGivenMetricApplicableForEstimateCC(row.metricName, rateCardVersion, isRenewal)
      }
    >
      <>
        <AddMetricNameDropdownRowForEstimateCC
          index={index}
          metricDimensionRefs={metricDimensionRefs}
          row={row}
        />
        <CommittedCapacityDimensionsGridForEstimateCC
          metricArrayHelpers={arrayHelpers}
          metricDimensionRefs={metricDimensionRefs}
          metricIndex={index}
          metricRow={row}
        />
        <AddNewMetricRowRelatedRowForEstimateCC
          metricDimensionRefs={metricDimensionRefs}
          row={row}
        />
        <Divider />
      </>
    </If>
  ));
};

export const EstimateCommittedCapacityConfigurationContainer = () => {
  const { values: formikValues, initialValues } = useFormikContext();
  const shouldFormBeDisabled = isAnythingEditedOnEstimatePageBesidesTheGivenForm(
    formikValues,
    initialValues,
    FORM_NAMES.COMMITTED_CAPACITY.COMMITTED_CAPACITY_FORM
  );

  const metricDimensionRefs = useRef({
    metricLevelArrayHelpers: null,
    dimensionArrayHelpersMap: {},
  });

  const configuredMetricRows = getConfiguredMetricRowsFromFormikValuesForEstimateCC(formikValues);

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <EstimateCommittedCapacityDescriberContainer />
      <EstimateCommittedCapacityAddNewMetricButton metricDimensionRefs={metricDimensionRefs} />
      <Spacer y={40} />
      <Grid style={{ paddingLeft: configuredMetricRows.length > 0 ? '3rem' : undefined }}>
        <FieldArray
          name={ESTIMATE_COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY}
          render={(arrayHelpers) => (
            <MetricRows
              arrayHelpers={arrayHelpers}
              configuredMetricRows={configuredMetricRows}
              metricDimensionRefs={metricDimensionRefs}
            />
          )}
        />
      </Grid>
    </PaddedAndRaisedSegment>
  );
};
